/* eslint-disable react/prop-types */
import React, {useState, useEffect} from 'react';
import {useMediaQuery} from 'react-responsive';
import {Link} from 'react-router-dom';
import {
  Button,
  Fab,
  Drawer,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HomeIcon from '@mui/icons-material/Home';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SavingsIcon from '@mui/icons-material/Savings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TimelineIcon from '@mui/icons-material/Timeline';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
} from 'chart.js';
import {Line, Doughnut} from 'react-chartjs-2';
import styled, {keyframes} from 'styled-components';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';

/**
 * Preview do "Relatório do mês" (ireit.com.br/financas/relatorio) — item 5
 * do plano em docs/FIINC_LANDING_SITE.md (enterpriseireit). Pedido explícito
 * do usuário (2026-09-12): mostrar, de um jeito atraente, como fica o
 * relatório completo que o Fiinc gera ao fechar o mês — sem usar nenhum dado
 * real da família dele (o relatório real dele tem 52 páginas; ele mandou
 * como referência de estrutura/conteúdo, não pra ser reproduzido aqui).
 *
 * TODO O DADO ABAIXO É FICTÍCIO E FIXO — família "Marina e Rafael Costa",
 * mês "out/26" (deliberadamente diferente do mês real usado como
 * referência), números inventados mas coerentes entre si (categorias somam
 * o total, disponível bate com renda-gastos etc). Mesmo princípio de
 * segurança do financas_demo/index.jsx: sem chamada de rede nenhuma.
 *
 * Não é uma réplica literal das 52 páginas do PDF real — é um resumo
 * curado, pensado pra venda: mostra 1-2 exemplos de cada seção (laudo,
 * resultado do mês, categorias, reserva, extratos considerados, metas,
 * independência financeira, anexo de lançamentos) o bastante pra passar a
 * sensação de "relatório de consultor de verdade", sem a exaustão de
 * rolar 52 páginas numa landing.
 */

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
);

const fadeUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;
const FadeUp = styled.div`
  animation: ${fadeUp} 0.5s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  verde: '#059669',
  vermelho: '#dc2626',
  rosa: '#db2777',
  bg: '#f4f6fa',
  text: '#1a1a2e',
  muted: '#6b7280',
  white: '#ffffff',
  border: '#e5e9f0',
};

const NAV_HEIGHT = 60;

const brl = v =>
  Number(v || 0).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'});

// ─── Dado fictício ──────────────────────────────────────────────────────
const MES_LABEL = 'out/26';
const FAMILIA = 'Marina e Rafael Costa';

const RESULTADO = {entrou: 14200, saiu: 10300};
RESULTADO.resultado = RESULTADO.entrou - RESULTADO.saiu;

const POR_PESSOA = [
  {nome: 'Marina Costa', entrou: 7100, saiu: 4900, cor: C.azul},
  {nome: 'Rafael Costa', saiu: 5400, entrou: 7100, cor: C.laranja},
];

// Pedido explícito do usuário (2026-09-12): basear no gráfico real de
// "Evolução de entradas e saídas" (EvolucaoEntradasSaidas.js) — e contar
// uma história real de ANTES × DEPOIS do Fiinc: os primeiros meses
// mostram um padrão "Dependente de Renda Extra" (linhas cruzando, saída
// ultrapassando entrada sem aviso) e os últimos meses mostram "Construtor
// de Patrimônio" (entrada e saída se afastando de forma saudável).
//
// Ajuste fino explícito do usuário (2026-09-12): nada de "aumento de
// renda" pra explicar a virada, e nada de corte abrupto de gasto de um mês
// pro outro (uma família que sempre gasta fixo não derruba isso da noite
// pro dia) — a RENDA fica sempre perto do mesmo valor (~R$ 13.6-14.2 mil,
// mesma faixa do RESULTADO fixo de out/26) do início ao fim, e é só o
// GASTO que conta a história, caindo aos poucos.
//
// Ajuste de realismo (2026-09-12, 2ª rodada): a 1ª versão tinha 16 meses
// seguidos no vermelho — o usuário apontou, corretamente, que isso não
// existe. Comparamos com o padrão real dele (leitura pontual e só-leitura
// via API pra calibrar a FORMA da curva, nunca os valores — os R$ daqui
// continuam 100% fictícios, ver header do arquivo): mesmo numa fase real
// de "dependente de renda extra" a família tem meses ok intercalados,
// nunca mais que ~2 meses seguidos no vermelho.
//
// Ajuste de realismo (2026-09-12, 3ª rodada): dois pedidos explícitos do
// usuário:
//  1) o vermelho (Saiu) nunca deveria passar de ~10% acima da renda
//     daquele mês, mesmo nos meses críticos — todo mês crítico fica entre
//     -7% e -9%, sempre com folga pro teto de 10%.
//  2) da fase "construindo patrimônio" em diante, a RENDA (verde) também
//     sobe um pouco, bem devagar — simulando que a família começou a
//     investir e esse aporte já entra como renda extra — em vez de só o
//     gasto caindo. Nas fases anteriores (crise + controle) a renda
//     continua igual: sempre perto do mesmo valor, sem nenhum "aumento de
//     renda" fake explicando a virada.
//
// Ajuste de realismo (2026-09-12, 4ª rodada): removidas as janelas de 6 e
// 24 meses — o usuário achou os dois extremos "fictícios demais" (24
// meses de pré-história inventada, 6 meses curto demais pra contar a
// história de virada). Fica só a janela de 12 meses (a que já existia
// como padrão), com a história completa: meses 1-4 "indisciplina" (crítico
// intercalado com respiros), meses 5-6 "controlando com dificuldade"
// (folga mínima), meses 7-8 "controlando sem dificuldade" (~15-25% de
// folga), meses 9-12 "construindo patrimônio" (folga acima de 30%, renda
// subindo e gasto caindo bem devagar) até bater com RESULTADO em out/26.
const EVOLUCAO_MESES = [
  'nov/25',
  'dez/25',
  'jan/26',
  'fev/26',
  'mar/26',
  'abr/26',
  'mai/26',
  'jun/26',
  'jul/26',
  'ago/26',
  'set/26',
  'out/26',
];
const EVOLUCAO_ENTROU = [
  13700, 13850, 13600, 13900, 13750, 13950, 13800, 14000, 14000, 14080, 14150,
  14200,
];
const EVOLUCAO_SAIU = [
  14900, 13300, 14850, 13100, 13600, 13500, 12200, 11200, 10600, 10500, 10400,
  10300,
];

// Portado literalmente de EvolucaoEntradasSaidas.js (enterpriseireit) —
// pedido explícito do usuário (2026-09-12) pra esse mockup gerar o gráfico
// EXATAMENTE do mesmo jeito que o produto real: em vez de pintar a área
// inteira de "Entrou" até o zero, só a FAIXA ENTRE as duas linhas é
// preenchida, com uma cor que interpola entre crítico/atenção/saudável
// conforme o quanto a entrada sobrou sobre a saída naquele trecho.
const CHART_VERDE = '#2e7d32';
const CHART_SAIDA = '#b3402f';
const SAUDE_EVOLUCAO_STOPS = [
  {p: -0.3, rgb: [183, 28, 28]},
  {p: 0, rgb: [230, 74, 25]},
  {p: 0.2, rgb: [249, 168, 37]},
  {p: 0.3, rgb: [46, 125, 50]},
];
const NIVEL_SAUDE_PONTO = {saudavel: 0.3, atencao: 0.1, critico: -0.15};
const percentualSobraEvolucao = (entrada, saida) => {
  if (saida > 0) return (entrada - saida) / saida;
  return entrada > 0 ? 1 : 0;
};
const corSaudeEvolucao = p => {
  const stops = SAUDE_EVOLUCAO_STOPS;
  const clamped = Math.max(stops[0].p, Math.min(stops[stops.length - 1].p, p));
  let i = 0;
  while (i < stops.length - 2 && clamped > stops[i + 1].p) i++;
  const a = stops[i];
  const b = stops[i + 1];
  const frac = b.p === a.p ? 0 : (clamped - a.p) / (b.p - a.p);
  return a.rgb.map((v, idx) => Math.round(v + (b.rgb[idx] - v) * frac));
};
const nivelSaudeEvolucao = p => {
  if (p < 0) return 'critico';
  if (p < 0.3) return 'atencao';
  return 'saudavel';
};

const CATEGORIAS = [
  {nome: 'Moradia', valor: 2600, cor: C.azul, icon: HomeIcon},
  {nome: 'Mercado', valor: 1450, cor: C.verde, icon: ShoppingCartIcon},
  {nome: 'Outras', valor: 3150, cor: '#9e9e9e', icon: SavingsIcon},
  {
    nome: 'Comer fora e delivery',
    valor: 950,
    cor: C.rosa,
    icon: RestaurantIcon,
  },
  {nome: 'Transporte', valor: 780, cor: C.roxo, icon: DirectionsCarIcon},
  {nome: 'Contas da casa', valor: 620, cor: '#0d9488', icon: HomeIcon},
  {
    nome: 'Saúde e farmácia',
    valor: 430,
    cor: '#e11d48',
    icon: LocalHospitalIcon,
  },
  {nome: 'Assinaturas', valor: 320, cor: '#7c3aed', icon: SubscriptionsIcon},
];

const EXTRATOS = [
  {banco: 'Nubank conta', pessoa: 'Marina', status: 'completo', pct: 100},
  {banco: 'Itaú cartão', pessoa: 'Rafael', status: 'completo', pct: 100},
  {
    banco: 'C6 conta',
    pessoa: 'Rafael',
    status: 'faltam',
    pct: 88,
    faltaTexto: 'Faltam: 29–31',
  },
];

const METAS = [
  {label: 'Renda regular', valor: 7100, tipo: 'entrada'},
  {label: 'Fixo essencial', valor: -1650, tipo: 'saida'},
  {label: 'Fixo não essencial', valor: -980, tipo: 'saida'},
  {label: 'Variável', valor: -2600, tipo: 'saida'},
  {label: 'Disponível', valor: 1870, tipo: 'total'},
];

const LANCAMENTOS_EXEMPLO = [
  {
    desc: 'Salário',
    data: '01/10',
    cat: 'Renda',
    banco: 'Nubank conta',
    valor: 7100,
    tipo: 'entrada',
  },
  {
    desc: 'Aluguel',
    data: '05/10',
    cat: 'Moradia',
    banco: 'Nubank conta',
    valor: -1650,
    tipo: 'saida',
  },
  {
    desc: 'Assaí Atacadista',
    data: '08/10',
    cat: 'Mercado',
    banco: 'Itaú cartão',
    valor: -412.6,
    tipo: 'saida',
  },
  {
    desc: 'Posto Ipiranga',
    data: '12/10',
    cat: 'Transporte',
    banco: 'Itaú cartão',
    valor: -180,
    tipo: 'saida',
  },
  {
    desc: 'Netflix',
    data: '14/10',
    cat: 'Assinaturas',
    banco: 'Itaú cartão',
    valor: -39.9,
    tipo: 'saida',
  },
  {
    desc: 'Drogaria São Paulo',
    data: '19/10',
    cat: 'Saúde e farmácia',
    banco: 'C6 conta',
    valor: -87.4,
    tipo: 'saida',
  },
];

// Pedido explícito do usuário (2026-09-12): o laudo escrito por IA é "a
// parte de maior valor pra família" — e ele vale tanto pro GRUPO quanto
// PRA CADA PESSOA (hábitos recomendados individualizados, não só um texto
// genérico de casal). Seções abaixo espelham as do relatório real: Saúde
// financeira, Padrão de gastos, Reserva e caixinhas, Dívidas,
// Independência financeira — cada uma com um parágrafo curto (versão
// resumida do laudo real, que tem 6+ parágrafos por seção).
const LAUDO_SECOES = [
  {
    titulo: 'Saúde financeira',
    icon: MonitorHeartIcon,
    cor: C.roxo,
    texto:
      'Diagnóstico atual: No Caminho Certo, melhorando. A renda ficou estável em torno de R$ 14 mil nos últimos 3 meses, e o gasto segue abaixo dela com folga consistente — diferente de meses atípicos isolados, é um padrão real. O ponto de atenção é que essa folga ainda é pequena perto de um imprevisto grande (uma emergência médica, por exemplo): antes de comprometer parte dela com um gasto maior, vale acelerar a reserva.',
  },
  {
    titulo: 'Padrão de gastos',
    icon: TimelineIcon,
    cor: C.azul,
    texto:
      'Mercado caiu de R$ 2.400 em maio pra R$ 1.450 em outubro — melhora real e consistente. Assinaturas somam R$ 320/mês em 4 serviços diferentes: vale revisar quais ainda são usadas de verdade. Comer fora oscila entre R$ 700 e R$ 1.300 conforme o mês — nada de alarmante ainda, mas fica de olho se ultrapassar R$ 1.500 por dois meses seguidos, que é quando um padrão vira hábito caro.',
  },
  {
    titulo: 'Reserva e caixinhas',
    icon: SavingsIcon,
    cor: C.verde,
    texto:
      'A reserva de emergência já cobre 23% do ideal (R$ 8.400 de R$ 36.000), guardando entre R$ 400 e R$ 500 por mês. Mantendo esse ritmo, a reserva completa em cerca de 14 meses — um prazo realista, sem precisar sacrificar o orçamento do mês a mês pra chegar lá mais rápido.',
  },
  {
    titulo: 'Dívidas',
    icon: CreditCardOffIcon,
    cor: '#c62828',
    texto:
      'Nenhuma dívida em aberto neste mês — sem fatura rotativa, sem parcelamento atrasado, sem juros de cartão aparecendo no histórico recente. É a base mais saudável pra sustentar qualquer meta maior (reserva, independência financeira) sem o risco de um débito corroendo o progresso por trás.',
  },
  {
    titulo: 'Independência financeira',
    icon: EmojiEventsIcon,
    cor: C.laranjaD,
    texto:
      'No ritmo atual de aporte (média de R$ 700/mês), a família chega à independência financeira em cerca de 22 anos. Aumentando o aporte mensal pra R$ 1.200 — viável assim que a reserva estiver completa — esse prazo cai pra menos de 16 anos. A ordem importa: reserva primeiro, aporte de longo prazo depois.',
  },
];

// Recomendações pro GRUPO — mesma seção "Hábitos recomendados para a
// família" do relatório real.
const HABITOS_FAMILIA = [
  'Manter o ritmo de guardar pra reserva mesmo nos meses de folga maior — é isso que tira o colchão do zero mais rápido, antes de qualquer outra meta.',
  'Revisar as 4 assinaturas ativas (R$ 320/mês) — cancelar o que não é mais usado de verdade libera espaço no orçamento sem sacrifício nenhum.',
  'Definir um limite mensal combinado pra "Comer fora e delivery" antes que a variação entre os meses vire um padrão difícil de prever.',
];

// Recomendações POR PESSOA — mesmo princípio do "Hábitos recomendados
// para João"/"para Gabriela" do relatório real: o laudo não fala só com o
// casal, fala com CADA pessoa sobre a fatia dela.
const HABITOS_PESSOAS = [
  {
    nome: 'Marina Costa',
    cor: C.azul,
    itens: [
      'Com renda mais estável que a de Rafael, Marina pode ser a âncora dos gastos fixos (aluguel, contas da casa) — evita que uma oscilação no lado dele comprometa o essencial.',
      'Guardar R$ 300/mês do disponível dela já ajuda a reserva do casal a crescer mais rápido, sem esperar decisão conjunta a cada mês.',
    ],
  },
  {
    nome: 'Rafael Costa',
    cor: C.laranja,
    itens: [
      'O gasto em "Comer fora e delivery" é o maior da família e concentrado nele — vale um teto mensal específico, não só a meta geral do casal.',
      'Com disponível estável nos últimos 3 meses, já dá pra começar um aporte pessoal pequeno em investimentos, mesmo antes da reserva do casal estar completa.',
    ],
  },
];

// ─── Peças pequenas reutilizadas ────────────────────────────────────────
const SectionTitle = ({eyebrow, titulo, sub}) => (
  <div style={{marginBottom: 18}}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 1.2,
        color: C.laranja,
        textTransform: 'uppercase',
      }}
    >
      {eyebrow}
    </span>
    <h2
      style={{
        fontSize: 22,
        fontWeight: 800,
        color: C.text,
        margin: '4px 0 6px',
      }}
    >
      {titulo}
    </h2>
    {sub && (
      <p style={{fontSize: 13.5, color: C.muted, margin: 0, maxWidth: 640}}>
        {sub}
      </p>
    )}
  </div>
);

const cardStyle = {
  background: C.white,
  borderRadius: 14,
  border: `1px solid ${C.border}`,
  boxShadow: '0 2px 10px rgba(16,24,40,0.05)',
  padding: 20,
};

const StatCard = ({label, valor, cor}) => (
  <div style={{...cardStyle, flex: 1, minWidth: 150}}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: C.muted,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
    <div style={{fontSize: 22, fontWeight: 800, color: cor, marginTop: 4}}>
      {brl(valor)}
    </div>
  </div>
);

const LegendDot = ({cor, label, valor}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
    <span
      style={{
        width: 9,
        height: 9,
        borderRadius: '50%',
        background: cor,
        flexShrink: 0,
      }}
    />
    <span style={{fontSize: 12.5, color: C.text, flex: 1}}>{label}</span>
    {valor != null && (
      <span style={{fontSize: 12.5, fontWeight: 700, color: C.text}}>
        {brl(valor)}
      </span>
    )}
  </div>
);

const BankCard = ({banco, pessoa, status, pct, faltaTexto}) => (
  <div style={{...cardStyle, padding: 14, minWidth: 190, flex: 1}}>
    <div
      style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}
    >
      {banco.toLowerCase().includes('cart') ? (
        <CreditCardIcon style={{fontSize: 17, color: C.azul}} />
      ) : (
        <AccountBalanceIcon style={{fontSize: 17, color: C.azul}} />
      )}
      <span style={{fontSize: 13, fontWeight: 700, color: C.text}}>
        {banco}
      </span>
    </div>
    <div
      style={{
        height: 6,
        borderRadius: 4,
        background: '#eef1f6',
        overflow: 'hidden',
        marginBottom: 6,
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          background: status === 'completo' ? C.verde : C.laranja,
          borderRadius: 4,
        }}
      />
    </div>
    <span
      style={{
        fontSize: 11.5,
        color: status === 'completo' ? C.verde : C.laranja,
        fontWeight: 700,
      }}
    >
      {status === 'completo' ? 'Mês completo' : faltaTexto}
    </span>
    <div style={{fontSize: 11, color: C.muted, marginTop: 4}}>{pessoa}</div>
  </div>
);

// ─── Página ─────────────────────────────────────────────────────────────
const FinancasRelatorio = () => {
  const isSmall = useMediaQuery({query: '(max-width: 750.98px)'});
  const isVerySmall = useMediaQuery({query: '(max-width:450.00px)'});
  const mobile = isSmall || isVerySmall;
  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  useEffect(() => {
    const t = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);

  const totalGastoCategorias = CATEGORIAS.reduce((s, c) => s + c.valor, 0);

  const evolucaoSaudeSerie = EVOLUCAO_ENTROU.map((entrada, i) => {
    const saida = EVOLUCAO_SAIU[i];
    const p = percentualSobraEvolucao(entrada, saida);
    return {p, nivel: nivelSaudeEvolucao(p)};
  });
  const corFaixaSegmentoEvolucao = ctx => {
    const i0 = ctx.p0DataIndex;
    const i1 = ctx.p1DataIndex;
    const s0 = evolucaoSaudeSerie[i0];
    const s1 = evolucaoSaudeSerie[i1];
    if (!s0 || !s1) return 'rgba(150,150,150,0.08)';
    const [r, g, b] = corSaudeEvolucao((s0.p + s1.p) / 2);
    return `rgba(${r},${g},${b},0.18)`;
  };

  return (
    <div
      style={{
        overflowX: 'hidden',
        width: '100%',
        background: C.bg,
        minHeight: '100vh',
      }}
    >
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 900}}
          size="small"
          color="primary"
          aria-label="voltar ao topo"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {mobile ? (
        <div
          style={{
            background: C.azul,
            height: NAV_HEIGHT,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'fixed',
            zIndex: 999,
            top: 0,
            left: 0,
            width: '100%',
          }}
        >
          <Button
            component={Link}
            to="/"
            style={{
              width: 95,
              height: 47,
              marginLeft: 12,
              backgroundImage: `url(${logo})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
          />
          <MenuIcon
            style={{color: '#ffffff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        </div>
      ) : (
        <Tab />
      )}
      {mobile && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      <div
        style={{
          maxWidth: 900,
          margin: '0 auto',
          padding: `${NAV_HEIGHT + 32}px 20px 70px`,
        }}
      >
        {/* ─── Intro / disclaimer ─────────────────────────────────────── */}
        <div style={{textAlign: 'center', marginBottom: 30}}>
          <Chip
            icon={<ShieldOutlinedIcon />}
            label="Exemplo — família e números 100% fictícios"
            sx={{'& .MuiChip-icon': {color: C.azul}}}
            style={{
              background: '#e6effa',
              color: C.azul,
              fontWeight: 700,
              marginBottom: 14,
            }}
          />
          <h1
            style={{
              fontSize: mobile ? 24 : 30,
              fontWeight: 800,
              color: C.text,
              margin: '0 0 8px',
            }}
          >
            O relatório que sua família recebe todo mês
          </h1>
          <p
            style={{
              fontSize: 14.5,
              color: C.muted,
              margin: '0 auto',
              maxWidth: 560,
            }}
          >
            Ao fechar o mês no Fiinc, vocês recebem um relatório completo — como
            se um consultor financeiro tivesse analisado tudo pessoalmente. Este
            é um exemplo com dado inventado, só pra mostrar o formato.
          </p>
        </div>

        {/* ─── "Capa" ─────────────────────────────────────────────────── */}
        <FadeUp
          style={{
            ...cardStyle,
            background: `linear-gradient(160deg, ${C.azul}, ${C.azulD})`,
            border: 'none',
            color: '#fff',
            padding: mobile ? 26 : 40,
            marginBottom: 20,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.4,
              color: C.laranja,
            }}
          >
            RELATÓRIO DO MÊS
          </span>
          <h2
            style={{
              fontSize: mobile ? 26 : 34,
              fontWeight: 800,
              margin: '8px 0 6px',
            }}
          >
            {FAMILIA}
          </h2>
          <p
            style={{
              fontStyle: 'italic',
              fontSize: 16,
              color: '#dbe6f5',
              margin: '0 0 14px',
            }}
          >
            Como {MES_LABEL} ficou, ponta a ponta
          </p>
          <p
            style={{fontSize: 12.5, color: '#b9cbe6', margin: 0, maxWidth: 460}}
          >
            Relatório construído a partir dos dados reais de Marina e Rafael,
            referente a {MES_LABEL}.
          </p>
        </FadeUp>

        {/* ─── Laudo financeiro ───────────────────────────────────────── */}
        <FadeUp delay="0.05s" style={{...cardStyle, marginBottom: 20}}>
          <SectionTitle
            eyebrow="Laudo financeiro"
            titulo="O que o mês contou"
            sub="Análise de out/26 por um analista financeiro sênior (IA)"
          />
          <div
            style={{
              borderLeft: `3px solid ${C.laranja}`,
              paddingLeft: 16,
              fontStyle: 'italic',
              color: C.text,
              fontSize: 14.5,
              lineHeight: 1.65,
              marginBottom: 16,
            }}
          >
            Outubro de 2026 fecha com renda de {brl(RESULTADO.entrou)} e gasto
            de {brl(RESULTADO.saiu)} — uma folga real de{' '}
            {brl(RESULTADO.resultado)}. O casal vem sustentando esse ritmo há 3
            meses seguidos, com a categoria Mercado controlada e nenhum sinal de
            juros de cartão. A boa notícia é que a reserva de emergência já
            cobre quase um quarto do ideal; o próximo passo é destinar parte
            dessa folga pra acelerar esse número antes do fim do ano.
          </div>
          <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
            <Chip
              label="Saúde financeira: No Caminho Certo"
              size="small"
              style={{background: '#e6f4ea', color: C.verde, fontWeight: 700}}
            />
            <Chip
              label="Melhorando há 3 meses"
              size="small"
              style={{background: '#f3f0fd', color: C.roxo, fontWeight: 700}}
            />
          </div>

          {/* Seção por seção, igual o laudo real (resumido aqui) — cada
              analista de IA escreve um parecer próprio pra cada tema. */}
          <div style={{marginTop: 20}}>
            {LAUDO_SECOES.map(s => (
              <Accordion
                key={s.titulo}
                disableGutters
                elevation={0}
                sx={{
                  border: `1px solid ${C.border}`,
                  borderRadius: '10px !important',
                  marginBottom: '10px',
                  '&:before': {display: 'none'},
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <s.icon style={{fontSize: 19, color: s.cor}} />
                    <span
                      style={{fontSize: 13.5, fontWeight: 700, color: C.text}}
                    >
                      {s.titulo}
                    </span>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <p
                    style={{
                      fontSize: 13,
                      lineHeight: 1.65,
                      color: C.muted,
                      margin: 0,
                    }}
                  >
                    {s.texto}
                  </p>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>

          {/* ─── Hábitos recomendados: grupo + cada pessoa ────────────────
              Pedido explícito do usuário (2026-09-12): deixar bem claro que
              o laudo não fala só com "o casal" — dá ação prática pro GRUPO
              e, separadamente, pra CADA MEMBRO sobre a fatia dele. Essa é a
              parte que ele descreveu como "maior valor pra família". */}
          <div style={{marginTop: 24}}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 4,
              }}
            >
              <GroupsIcon style={{fontSize: 20, color: C.text}} />
              <span style={{fontSize: 15, fontWeight: 800, color: C.text}}>
                Hábitos recomendados
              </span>
            </div>
            <p style={{fontSize: 12.5, color: C.muted, margin: '0 0 14px'}}>
              Ações práticas pro mês que vem — uma lista pro grupo, e uma lista
              própria pra cada pessoa, sobre a parte que só ela pode mudar.
            </p>

            <div style={{...cardStyle, background: C.bg, marginBottom: 14}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <GroupsIcon style={{fontSize: 16, color: C.azul}} />
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: C.azul,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  Pra família
                </span>
              </div>
              <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                {HABITOS_FAMILIA.map((h, i) => (
                  <div key={i} style={{display: 'flex', gap: 10}}>
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: C.azul,
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      {i + 1}
                    </span>
                    <span
                      style={{fontSize: 12.5, lineHeight: 1.55, color: C.text}}
                    >
                      {h}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{display: 'flex', gap: 14, flexWrap: 'wrap'}}>
              {HABITOS_PESSOAS.map(p => (
                <div
                  key={p.nome}
                  style={{...cardStyle, flex: 1, minWidth: 260}}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 10,
                    }}
                  >
                    <Avatar
                      sx={{width: 26, height: 26, fontSize: 12, bgcolor: p.cor}}
                    >
                      {p.nome.charAt(0)}
                    </Avatar>
                    <span
                      style={{fontSize: 12.5, fontWeight: 700, color: C.text}}
                    >
                      Pra {p.nome.split(' ')[0]}
                    </span>
                  </div>
                  <div
                    style={{display: 'flex', flexDirection: 'column', gap: 8}}
                  >
                    {p.itens.map((it, i) => (
                      <div key={i} style={{display: 'flex', gap: 8}}>
                        <PersonIcon
                          style={{
                            fontSize: 14,
                            color: p.cor,
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 12,
                            lineHeight: 1.55,
                            color: C.muted,
                          }}
                        >
                          {it}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>

        {/* ─── Resultado do mês ───────────────────────────────────────── */}
        <FadeUp delay="0.1s" style={{marginBottom: 20}}>
          <SectionTitle
            eyebrow="O resultado do mês"
            titulo={`Quanto entrou e quanto saiu em ${MES_LABEL}`}
          />
          <div
            style={{
              display: 'flex',
              gap: 14,
              flexWrap: 'wrap',
              marginBottom: 16,
            }}
          >
            <StatCard label="Entrou" valor={RESULTADO.entrou} cor={C.verde} />
            <StatCard label="Saiu" valor={RESULTADO.saiu} cor={C.laranja} />
            <StatCard
              label="Resultado"
              valor={RESULTADO.resultado}
              cor={C.azul}
            />
          </div>
          <div style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
            <div style={{...cardStyle, flex: 2, minWidth: 280}}>
              <span style={{fontSize: 12, fontWeight: 700, color: C.muted}}>
                Evolução de entradas e saídas — últimos 12 meses
              </span>
              <p
                style={{
                  fontSize: 11.5,
                  color: C.muted,
                  margin: '6px 0 0',
                  lineHeight: 1.5,
                }}
              >
                Compare mês a mês pra ver se em algum momento os gastos passaram
                da renda (as linhas se cruzam).
              </p>
              <div style={{height: 170, marginTop: 10}}>
                <Line
                  data={{
                    labels: EVOLUCAO_MESES,
                    datasets: [
                      {
                        label: 'Entrou',
                        data: EVOLUCAO_ENTROU,
                        borderColor: CHART_VERDE,
                        backgroundColor: CHART_VERDE,
                        pointBackgroundColor: CHART_VERDE,
                        tension: 0.3,
                        pointRadius: 3,
                        borderWidth: 2,
                        fill: 1,
                        segment: {backgroundColor: corFaixaSegmentoEvolucao},
                      },
                      {
                        label: 'Saiu',
                        data: EVOLUCAO_SAIU,
                        borderColor: CHART_SAIDA,
                        backgroundColor: CHART_SAIDA,
                        pointBackgroundColor: CHART_SAIDA,
                        tension: 0.3,
                        pointRadius: 3,
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {mode: 'index', intersect: false},
                    plugins: {
                      legend: {display: false},
                      tooltip: {enabled: false},
                    },
                    scales: {
                      x: {
                        grid: {display: false},
                        ticks: {
                          font: {size: 10},
                          color: C.muted,
                          maxRotation: 0,
                          autoSkip: true,
                          maxTicksLimit: 6,
                        },
                      },
                      y: {display: false},
                    },
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  marginTop: 10,
                  flexWrap: 'wrap',
                }}
              >
                <LegendDot cor={CHART_VERDE} label="Entrou" />
                <LegendDot cor={CHART_SAIDA} label="Saiu" />
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  marginTop: 6,
                  flexWrap: 'wrap',
                }}
              >
                {Object.entries(NIVEL_SAUDE_PONTO).map(([nivel, p]) => {
                  const [r, g, b] = corSaudeEvolucao(p);
                  const rotulo = {
                    saudavel: 'Saudável',
                    atencao: 'Atenção',
                    critico: 'Crítico',
                  }[nivel];
                  return (
                    <div
                      key={nivel}
                      style={{display: 'flex', alignItems: 'center', gap: 6}}
                    >
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: 2,
                          background: `rgb(${r},${g},${b})`,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{fontSize: 11, color: C.muted}}>
                        {rotulo}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: '1px dashed #e2e6ee',
                }}
              >
                <Chip
                  label="No início: Dependente de Renda Extra"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 10.5,
                    bgcolor: `${C.vermelho}18`,
                    color: C.vermelho,
                  }}
                />
                <ArrowForwardIcon style={{fontSize: 15, color: C.muted}} />
                <Chip
                  label="Hoje: Construtor de Patrimônio"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 10.5,
                    bgcolor: `${C.verde}18`,
                    color: C.verde,
                  }}
                />
              </div>
            </div>
            <div style={{...cardStyle, flex: 1, minWidth: 220}}>
              <span style={{fontSize: 12, fontWeight: 700, color: C.muted}}>
                Quem trouxe/gastou quanto
              </span>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  marginTop: 12,
                }}
              >
                {POR_PESSOA.map(p => (
                  <div key={p.nome}>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: C.text,
                        marginBottom: 4,
                      }}
                    >
                      {p.nome}
                    </div>
                    <LegendDot cor={C.verde} label="Entrou" valor={p.entrou} />
                    <div style={{height: 4}} />
                    <LegendDot cor={C.laranja} label="Saiu" valor={p.saiu} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>

        {/* ─── Gasto por categoria ────────────────────────────────────── */}
        <FadeUp delay="0.15s" style={{...cardStyle, marginBottom: 20}}>
          <SectionTitle
            eyebrow="Pra onde foi o dinheiro"
            titulo={`Gasto por categoria em ${MES_LABEL}`}
          />
          <div
            style={{
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: mobile ? 160 : 190,
                height: mobile ? 160 : 190,
                flexShrink: 0,
                margin: '0 auto',
              }}
            >
              <Doughnut
                data={{
                  labels: CATEGORIAS.map(c => c.nome),
                  datasets: [
                    {
                      data: CATEGORIAS.map(c => c.valor),
                      backgroundColor: CATEGORIAS.map(c => c.cor),
                      borderWidth: 2,
                      borderColor: '#fff',
                    },
                  ],
                }}
                options={{
                  plugins: {
                    legend: {display: false},
                    tooltip: {enabled: false},
                  },
                  cutout: '68%',
                }}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 240,
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
              }}
            >
              {CATEGORIAS.map(c => (
                <LegendDot
                  key={c.nome}
                  cor={c.cor}
                  label={`${c.nome} — ${Math.round(
                    (c.valor / totalGastoCategorias) * 100,
                  )}%`}
                  valor={c.valor}
                />
              ))}
            </div>
          </div>
        </FadeUp>

        {/* ─── Reserva de emergência ──────────────────────────────────── */}
        <FadeUp delay="0.2s" style={{...cardStyle, marginBottom: 20}}>
          <SectionTitle
            eyebrow="Reserva de emergência"
            titulo="Quanto falta pro colchão ideal"
            sub="Calculada automaticamente a partir de 6 meses do que a família costuma gastar em coisas essenciais."
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 8,
            }}
          >
            <SavingsIcon style={{color: C.verde}} />
            <span style={{fontSize: 18, fontWeight: 800, color: C.text}}>
              {brl(8400)}{' '}
              <span style={{color: C.muted, fontWeight: 500, fontSize: 13}}>
                de {brl(36000)}
              </span>
            </span>
          </div>
          <LinearProgress
            variant="determinate"
            value={23}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#eef1f6',
              '& .MuiLinearProgress-bar': {
                backgroundColor: C.verde,
                borderRadius: 4,
              },
            }}
          />
          <span style={{fontSize: 11.5, color: C.muted}}>
            23% da reserva ideal já formada
          </span>
        </FadeUp>

        {/* ─── Extratos considerados ──────────────────────────────────── */}
        <FadeUp delay="0.25s" style={{marginBottom: 20}}>
          <SectionTitle
            eyebrow="De onde vêm os dados"
            titulo="Extratos considerados"
            sub="Nubank, Itaú, Bradesco, C6, Flash, faturas em PDF de qualquer banco — a IA identifica o formato sozinha."
          />
          <div style={{display: 'flex', gap: 14, flexWrap: 'wrap'}}>
            {EXTRATOS.map(e => (
              <BankCard key={e.banco} {...e} />
            ))}
          </div>
        </FadeUp>

        {/* ─── Metas ──────────────────────────────────────────────────── */}
        <FadeUp delay="0.3s" style={{...cardStyle, marginBottom: 20}}>
          <SectionTitle
            eyebrow="Metas"
            titulo="Como ficaram definidas pro próximo mês"
            sub="Exemplo simplificado — Marina Costa"
          />
          <div>
            {METAS.map(m => (
              <div
                key={m.label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: `1px solid ${C.border}`,
                  fontWeight: m.tipo === 'total' ? 800 : 500,
                }}
              >
                <span style={{fontSize: 13.5, color: C.text}}>{m.label}</span>
                <span
                  style={{
                    fontSize: 13.5,
                    color:
                      m.tipo === 'entrada'
                        ? C.verde
                        : m.tipo === 'saida'
                        ? C.laranjaD
                        : C.azul,
                  }}
                >
                  {m.tipo === 'saida' ? '− ' : m.tipo === 'entrada' ? '+ ' : ''}
                  {brl(Math.abs(m.valor))}
                </span>
              </div>
            ))}
          </div>
        </FadeUp>

        {/* ─── Independência financeira ───────────────────────────────── */}
        <FadeUp delay="0.35s" style={{...cardStyle, marginBottom: 20}}>
          <SectionTitle
            eyebrow="Independência financeira"
            titulo="Quando a família será independente"
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <TrendingUpIcon style={{fontSize: 34, color: C.azul}} />
            <p
              style={{
                fontSize: 13.5,
                color: C.text,
                margin: 0,
                flex: 1,
                minWidth: 240,
              }}
            >
              No ritmo atual de aportes, a família alcança a independência
              financeira em <b>cerca de 22 anos</b>. Aumentando o aporte mensal
              em {brl(500)}, esse prazo cai pra menos de 16 anos.
            </p>
          </div>
        </FadeUp>

        {/* ─── Anexo — lançamentos ────────────────────────────────────── */}
        <FadeUp delay="0.4s" style={{...cardStyle, marginBottom: 30}}>
          <SectionTitle
            eyebrow="Anexo"
            titulo="Todos os lançamentos, um por um"
            sub="No relatório completo, todas as transações do mês aparecem organizadas por dia e por categoria — aqui, uma amostra."
          />
          <div>
            {LANCAMENTOS_EXEMPLO.map((l, i) => {
              const cat = CATEGORIAS.find(c => c.nome === l.cat);
              const Icon = cat ? cat.icon : CheckCircleIcon;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom:
                      i < LANCAMENTOS_EXEMPLO.length - 1
                        ? `1px solid ${C.border}`
                        : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: `${cat ? cat.cor : C.muted}1a`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      style={{fontSize: 16, color: cat ? cat.cor : C.muted}}
                    />
                  </div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div
                      style={{fontSize: 13.5, fontWeight: 600, color: C.text}}
                    >
                      {l.desc}
                    </div>
                    <div style={{fontSize: 11.5, color: C.muted}}>
                      {l.data} · {l.cat} · {l.banco}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: l.tipo === 'entrada' ? C.verde : C.text,
                      flexShrink: 0,
                    }}
                  >
                    {l.tipo === 'entrada' ? '+ ' : '− '}
                    {brl(Math.abs(l.valor))}
                  </span>
                </div>
              );
            })}
          </div>
        </FadeUp>

        {/* ─── CTA final ──────────────────────────────────────────────── */}
        <FadeUp
          delay="0.45s"
          style={{
            ...cardStyle,
            textAlign: 'center',
            background: `linear-gradient(135deg, ${C.azul}, ${C.roxo})`,
            border: 'none',
            color: '#fff',
            padding: mobile ? 26 : 36,
          }}
        >
          <h2
            style={{
              fontSize: mobile ? 20 : 24,
              fontWeight: 800,
              margin: '0 0 8px',
            }}
          >
            Quer um relatório assim da sua família?
          </h2>
          <p
            style={{
              fontSize: 14,
              color: '#e4ecfb',
              margin: '0 0 20px',
              maxWidth: 480,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Feche o mês e gere na hora — baixe em PDF ou mande por e-mail quando
            quiser, sem precisar pedir nada a ninguém.
          </p>
          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              component={Link}
              to="/cadastro_financas"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              style={{textTransform: 'none', fontWeight: 700, borderRadius: 12}}
              sx={{bgcolor: C.laranja, ':hover': {bgcolor: C.laranjaD}}}
            >
              Criar minha conta grátis
            </Button>
            <Button
              component={Link}
              to="/financas/demo"
              variant="outlined"
              style={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 12,
                borderColor: '#fff',
                color: '#fff',
              }}
            >
              Ver o app funcionando
            </Button>
          </div>
        </FadeUp>
      </div>

      <Footer />
    </div>
  );
};

export default FinancasRelatorio;
